import React from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

/**
 * Unified infinite scroll loading indicator component
 * Fixes excessive spacing and provides consistent behavior across all components
 */
const InfiniteScrollIndicator = ({
  loading = false,
  hasMore = false,
  dataLength = 0,
  error = null,
  lastElementRef = null,
  loadingText = "applicationTable.loadingMore",
  noMoreText = "applicationTable.noMoreCandidates",
  errorText = "applicationTable.loadingError",
  minHeight = '40px', // Reduced from 60px to fix excessive spacing
  showNoMoreMessage = true,
}) => {
  const { t } = useTranslation();

  // Don't render anything if there's no data and no loading
  if (dataLength === 0 && !loading) {
    return null;
  }

  return (
    <Box
      ref={lastElementRef}
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        py: 2, // Reduced from py: 3 to fix excessive spacing
        minHeight: minHeight,
        // Remove excessive bottom margin
        mb: 0,
      }}
    >
      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={20} /> {/* Reduced from 24 to 20 */}
          <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.875rem' }}>
            {t(loadingText, "Loading more...")}
          </Typography>
        </Box>
      )}
      
      {!loading && !hasMore && dataLength > 0 && showNoMoreMessage && (
        <Typography 
          variant="body2" 
          color="textSecondary" 
          sx={{ 
            fontStyle: 'italic',
            fontSize: '0.875rem',
            // Remove excessive spacing
            py: 0,
            my: 0,
          }}
        >
          {t(noMoreText, "No more items to load")}
        </Typography>
      )}
      
      {error && (
        <Typography 
          variant="body2" 
          color="error" 
          sx={{ 
            fontSize: '0.875rem',
            // Remove excessive spacing
            py: 0,
            my: 0,
          }}
        >
          {t(errorText, "Error loading items. Please try again.")}
        </Typography>
      )}
    </Box>
  );
};

export default InfiniteScrollIndicator;
